import { useState, type FormEvent } from 'react'
import './tool-run.css'

/**
 * Binds a reference tool page's own run form (`form.run`, ported verbatim
 * from public/pixelmojo-rebuild/pixelmojo-tools) to the live runner: field
 * values by position (f0, f1, …), submit, the example chip and any extra
 * action button. The live page keeps its run logic, gates and paywall.
 */
export interface ToolRunBinding {
  values: string[]
  onChange: (index: number, value: string) => void
  onBlur?: (index: number) => void
  onSubmit: (e: FormEvent<HTMLFormElement>) => void
  onExample?: () => void
  onAction?: () => void
  loading?: boolean
  error?: string | null
}

/**
 * The run form's submit handler. A submit while a run is in flight is
 * ignored, and the submit button is disabled for the same window (as
 * ToolEntryHero's was), so a second click cannot start a duplicate run on
 * any binding.
 */
export function runSubmit(run?: ToolRunBinding) {
  if (!run) return undefined
  return (e: FormEvent<HTMLFormElement>) => {
    if (run.loading) {
      e.preventDefault()
      return
    }
    run.onSubmit(e)
  }
}

/** The URL check ToolEntryHero ran before handing off to the page. */
export function validateToolUrl(value: string): string | null {
  if (!value.trim()) return 'Please enter a URL.'
  let test = value.trim()
  if (!test.startsWith('http://') && !test.startsWith('https://')) {
    test = `https://${test}`
  }
  try {
    const parsed = new URL(test)
    if (!parsed.hostname.includes('.')) {
      return 'Please enter a valid domain (e.g. example.com).'
    }
  } catch {
    return 'Please enter a valid URL.'
  }
  return null
}

/**
 * Single-URL tools: the same contract ToolEntryHero had. Validate locally,
 * then hand the trimmed URL to the page's submit handler.
 */
export function useUrlRun({
  example,
  isLoading,
  error,
  onSubmit,
}: {
  example: string
  isLoading?: boolean
  error?: string | null
  onSubmit: (url: string) => void
}): ToolRunBinding {
  const [url, setUrl] = useState('')
  const [invalid, setInvalid] = useState<string | null>(null)
  return {
    values: [url],
    onChange: (_i, v) => {
      setUrl(v)
      if (invalid) setInvalid(null)
    },
    onSubmit: e => {
      e.preventDefault()
      const problem = validateToolUrl(url)
      if (problem) {
        setInvalid(problem)
        return
      }
      setInvalid(null)
      onSubmit(url.trim())
    },
    onExample: () => {
      setUrl(example)
      setInvalid(null)
    },
    loading: isLoading,
    error: invalid ?? error ?? null,
  }
}
